import React from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { LogOut, Menu, X, Package } from 'lucide-react';
import { AuthProvider, useAuth } from './contexts/AuthContextMySQL';
import { ChatProvider } from './contexts/ChatContext';
import { NotificationProvider } from './contexts/NotificationContext';

// Pages
import LandingPage from './LandingPage';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import BuyForm from './pages/BuyForm';
import SellForm from './pages/SellForm';
import DemoPage from './pages/DemoPage';
import PaymentPage from './pages/PaymentPage';
import CustomerServiceChat from './pages/CustomerServiceChat';
import OrderHistory from './pages/OrderHistory';
import AdminDashboard from './pages/AdminDashboard';
import AdminChat from './pages/AdminChat';

import PricesPage from './pages/site/PricesPage';
import BuyGuidePage from './pages/site/BuyGuidePage';
import SellGuidePage from './pages/site/SellGuidePage';
import ServersPage from './pages/site/ServersPage';
import GamePage from './pages/site/GamePage';
import ServerPage from './pages/site/ServerPage';
import ComparisonPage from './pages/site/ComparisonPage';
import DofusMarocPage from './pages/site/DofusMarocPage';
import FaqPage from './pages/site/FaqPage';
import NotFoundPage from './pages/site/NotFoundPage';
import ArticlesPage from './pages/site/ArticlesPage';
import ArticlePage from './pages/site/ArticlePage';
import TermsPage from './pages/site/TermsPage';
import PrivacyPage from './pages/site/PrivacyPage';

// Components
import ChatWidget from './components/ChatWidget';
import SiteFooter from './site/SiteFooter';
import { GAMES } from './data/servers';
import { PAGES, MAIN_LINKS, gamePath } from './site/routes';

const NAV_LINKS = [
  { to: PAGES.buy, label: 'Acheter' },
  { to: PAGES.sell, label: 'Vendre' },
  { to: PAGES.prices, label: 'Prix' },
  { to: PAGES.servers, label: 'Serveurs' },
  { to: PAGES.articles, label: 'Articles' },
  { to: PAGES.faq, label: 'FAQ' },
];

// Remonte en haut de page à chaque changement de page
function ScrollToTop() {
  const { pathname } = useLocation();
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

// Navigation Bar Component
function Navbar() {
  const { isAuthenticated, user, isAdmin, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleDashboardClick = () => {
    if (isAdmin) {
      navigate('/admin');
    } else {
      navigate('/order-history');
    }
  };

  return (
    <nav className="border-b border-yellow-500/20 bg-black/50 backdrop-blur-md sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 sm:gap-3">
            <img
              src="/logo512.png"
              alt="AV Kamas"
              className="w-9 h-9 lg:w-10 lg:h-10 rounded-full shadow-[0_0_15px_rgba(250,204,21,0.4)]"
            />
            <span className="text-xl lg:text-2xl font-bold tracking-wider text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 to-yellow-600">
              AV <span className="text-white">KAMAS</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-5 xl:gap-7">
            {NAV_LINKS.map(link => (
              <Link key={link.to} to={link.to} className="text-gray-400 hover:text-yellow-400 transition-colors font-medium">
                {link.label}
              </Link>
            ))}
            
            {isAuthenticated ? (
              <div className="flex items-center gap-4 pl-5 border-l border-gray-800">
                <button
                  onClick={handleDashboardClick}
                  className="text-gray-400 hover:text-yellow-400 transition-colors font-medium flex items-center gap-1"
                >
                  <Package size={16} />
                  {isAdmin ? 'Admin Dashboard' : 'Mes Commandes'}
                </button>
                <span className="hidden xl:inline text-gray-400">{user?.name}</span>
                {isAdmin && (
                  <span className="px-2 py-1 bg-red-500/20 text-red-400 rounded-full text-xs font-medium">
                    Admin
                  </span>
                )}
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-2 text-gray-400 hover:text-red-400 transition-colors"
                >
                  <LogOut size={18} />
                  Déconnexion
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-4 pl-5 border-l border-gray-800">
                <Link to="/login" className="text-gray-400 hover:text-yellow-400 transition-colors font-medium">
                  Connexion
                </Link>
                <Link to="/register">
                  <span className="bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-400 hover:to-yellow-500 text-black px-5 py-2.5 rounded-lg font-bold transition-all shadow-[0_0_15px_rgba(250,204,21,0.3)]">
                    Inscription
                  </span>
                </Link>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
            aria-expanded={mobileMenuOpen}
            className="lg:hidden p-2 -mr-2 text-gray-400 hover:text-white"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            className="lg:hidden bg-gray-900 border-t border-gray-800 overflow-hidden"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
          >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 grid grid-cols-2 gap-2 text-sm">
              <Link
                to="/buy"
                onClick={() => setMobileMenuOpen(false)}
                className="py-2.5 text-center rounded-lg bg-yellow-500 text-black font-semibold"
              >
                Acheter
              </Link>
              <Link
                to="/sell"
                onClick={() => setMobileMenuOpen(false)}
                className="py-2.5 text-center rounded-lg bg-gray-800 text-white font-semibold"
              >
                Vendre
              </Link>

              <ul className="col-span-2 grid grid-cols-2 gap-x-3 border-y border-gray-800 py-2 my-1">
                {MAIN_LINKS.map(link => (
                  <li key={link.to}>
                    <Link to={link.to} onClick={() => setMobileMenuOpen(false)} className="block py-1.5 text-gray-300 hover:text-yellow-400">
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
              
              {isAuthenticated ? (
                <>
                  <div className="col-span-2 flex items-center gap-2 py-1 text-gray-400">
                    <span className="truncate">{user?.name}</span>
                    {isAdmin && (
                      <span className="px-2 py-0.5 bg-red-500/20 text-red-400 rounded-full text-xs font-medium">
                        Admin
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      handleDashboardClick();
                      setMobileMenuOpen(false);
                    }}
                    className="py-2.5 rounded-lg border border-gray-700 text-gray-300 hover:text-yellow-400"
                  >
                    {isAdmin ? 'Admin Dashboard' : 'Mes Commandes'}
                  </button>
                  <button
                    onClick={() => {
                      handleLogout();
                      setMobileMenuOpen(false);
                    }}
                    className="py-2.5 rounded-lg border border-red-500/30 text-red-400"
                  >
                    Déconnexion
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    onClick={() => setMobileMenuOpen(false)}
                    className="py-2.5 text-center rounded-lg border border-gray-700 text-gray-300 hover:text-yellow-400"
                  >
                    Connexion
                  </Link>
                  <Link
                    to="/register"
                    onClick={() => setMobileMenuOpen(false)}
                    className="py-2.5 text-center rounded-lg border border-yellow-500/40 text-yellow-400"
                  >
                    Inscription
                  </Link>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}

// Layout with Navbar and Chat
function Layout({ children }) {
  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <Navbar />
      {children}
      <SiteFooter />
      <ChatWidget />
    </div>
  );
}

// Private / utility pages: keep them out of search results
const PRIVATE_PAGES = {
  '/login': 'Connexion',
  '/register': 'Créer un compte',
  '/forgot-password': 'Mot de passe oublié',
  '/reset-password': 'Nouveau mot de passe',
  '/payment': 'Paiement',
  '/payment-success': 'Paiement',
  '/customer-service': 'Service client',
  '/order-history': 'Mes commandes',
  '/demo': 'Démo',
};

function PrivatePageSeo() {
  const { pathname } = useLocation();
  const label = pathname.startsWith('/admin') ? 'Administration' : PRIVATE_PAGES[pathname];
  const isPrivate = !!label;

  React.useEffect(() => {
    if (!isPrivate) return;
    document.title = `${label} | AV Kamas`;
    document.head.querySelector('meta[name="robots"]')?.setAttribute('content', 'noindex, nofollow');
  }, [isPrivate, label]);

  return null;
}

// Main App Routes
function AppRoutes() {
  return (
    <>
    <ScrollToTop />
    <PrivatePageSeo />
    <Routes>
      <Route path="/" element={<Layout><LandingPage /></Layout>} />
      <Route path={PAGES.prices} element={<Layout><PricesPage /></Layout>} />
      <Route path={PAGES.buyGuide} element={<Layout><BuyGuidePage /></Layout>} />
      <Route path={PAGES.sellGuide} element={<Layout><SellGuidePage /></Layout>} />
      <Route path={PAGES.servers} element={<Layout><ServersPage /></Layout>} />
      <Route path={PAGES.comparison} element={<Layout><ComparisonPage /></Layout>} />
      <Route path={PAGES.dofusMaroc} element={<Layout><DofusMarocPage /></Layout>} />
      <Route path={PAGES.faq} element={<Layout><FaqPage /></Layout>} />
      <Route path={PAGES.articles} element={<Layout><ArticlesPage /></Layout>} />
      <Route path={`${PAGES.articles}/:slug`} element={<Layout><ArticlePage /></Layout>} />
      <Route path={PAGES.terms} element={<Layout><TermsPage /></Layout>} />
      <Route path={PAGES.privacy} element={<Layout><PrivacyPage /></Layout>} />
      {GAMES.map(game => (
        <React.Fragment key={game}>
          <Route path={gamePath(game)} element={<Layout><GamePage game={game} /></Layout>} />
          <Route path={`${gamePath(game)}/:slug`} element={<Layout><ServerPage game={game} /></Layout>} />
        </React.Fragment>
      ))}
      <Route path="/buy" element={<Layout><BuyForm /></Layout>} />
      <Route path="/sell" element={<Layout><SellForm /></Layout>} />
      <Route path="/payment" element={<Layout><PaymentPage /></Layout>} />
      <Route path="/payment-success" element={<Layout><PaymentPage /></Layout>} />
      <Route path="/customer-service" element={<Layout><CustomerServiceChat /></Layout>} />
      <Route path="/order-history" element={<Layout><OrderHistory /></Layout>} />
      <Route path="/admin" element={<AdminDashboard />} />
      <Route path="/admin/chat/:chatId" element={<AdminChat />} />
      <Route path="/demo" element={<DemoPage />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="*" element={<Layout><NotFoundPage /></Layout>} />
    </Routes>
    </>
  );
}

// Main App Component
export default function App() {
  return (
    <Router>
      <AuthProvider>
        <NotificationProvider>
          <ChatProvider>
            <AppRoutes />
          </ChatProvider>
        </NotificationProvider>
      </AuthProvider>
    </Router>
  );
}

